import { useCallback, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate, type LinkProps } from 'react-router';
import { usePainel, type Painel } from '../layout/tela-dividida';

/**
 * Lista de 30/10 B (item 6): os botões "Voltar" do sistema voltam à tela em que a pessoa estava (o histórico do app); aberta a
 * página direto (link colado, nova aba, recarga sem entrada anterior no app), vão à tela-mãe, como antes. Um ponto só para
 * todos (botão e link):
 *
 *  - Na janela, o roteador do navegador guarda o índice da entrada (`history.state.idx`): acima de 0 há tela do app antes desta.
 *    Navegação por substituição (o `lado` da tela dividida, redirecionamentos) não cria entrada, então não conta.
 *  - No painel ao lado (tela dividida), o roteador em memória começa na chave `default`; qualquer chave diferente é navegação
 *    feita dentro do painel.
 *  - O link continua com o `href` da tela-mãe (Ctrl, Shift, botão do meio e "abrir em nova aba" seguem o navegador); só o
 *    clique simples volta pelo histórico.
 */
export function haTelaAnteriorNoApp(painel: Painel, chaveDaRota: string): boolean {
  if (painel === 'lado') return chaveDaRota !== 'default';
  if (typeof window === 'undefined') return false;
  return (((window.history.state as { idx?: number } | null)?.idx ?? 0) as number) > 0;
}

export function useVoltar(mae: string) {
  const navegar = useNavigate();
  const painel = usePainel();
  const { key } = useLocation();
  // O histórico só é lido no clique (a página também é montada fora do navegador, nos testes de servidor).
  const voltar = useCallback(() => {
    if (haTelaAnteriorNoApp(painel, key)) void navegar(-1);
    else void navegar(mae);
  }, [navegar, painel, key, mae]);
  /** Para o `onClick` de um `<Link to={mae}>`: o clique simples volta pelo histórico quando há tela anterior no app. */
  const aoClicarLink = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!haTelaAnteriorNoApp(painel, key)) return;
      e.preventDefault();
      void navegar(-1);
    },
    [navegar, painel, key],
  );
  return { para: mae, voltar, aoClicarLink };
}

/** O link "Voltar" de sempre (`href` da tela-mãe), voltando pelo histórico do app no clique simples. */
export function LinkVoltar({ para, onClick, ...resto }: Omit<LinkProps, 'to'> & { para: string }) {
  const { aoClicarLink } = useVoltar(para);
  return (
    <Link
      {...resto}
      to={para}
      onClick={(e) => {
        onClick?.(e);
        aoClicarLink(e);
      }}
    />
  );
}
